import { ToolDefinition, ToolRegistry, ToolExecutionResult, ToolSchema } from '../types';
import { portfolioToolSchemas } from '../services/toolDefinitions';
import { executeGetPortfolioBalances } from './portfolioBalances';
import { executeGetPortfolioPerformance } from './portfolioPerformance';
import { executeGetRegionalAllocation } from './regionalAllocation';
import { executeGetCurrentHoldings } from './currentHoldings';
import { executeGetCellarStats } from './cellarStats';
import { executeGetCellarInsights } from './cellarInsights';
import { executeGetSoldHoldings } from './soldHoldings';
import { executeGetExternalHoldings } from './externalHoldings';
import { executeGetManagementFees } from './managementFees';
import { executeGetUserProfile } from './userProfile';
import { executeGetUserPreferences } from './userPreferences';
import { executeGetPortalCurrentHoldings } from './portalCurrentHoldings';
import { executeGetReturnValuations } from './returnValuations';

const toolExecutors: Record<string, (args: Record<string, unknown>) => Promise<ToolExecutionResult>> = {
  get_portfolio_balances: executeGetPortfolioBalances,
  get_portfolio_performance: executeGetPortfolioPerformance,
  get_regional_allocation: executeGetRegionalAllocation,
  get_current_holdings: executeGetCurrentHoldings,
  get_cellar_stats: executeGetCellarStats,
  get_cellar_insights: executeGetCellarInsights,
  get_sold_holdings: executeGetSoldHoldings,
  get_external_holdings: executeGetExternalHoldings,
  get_management_fees: executeGetManagementFees,
  get_user_profile: executeGetUserProfile,
  get_user_preferences: executeGetUserPreferences,
  get_detailed_holdings: executeGetPortalCurrentHoldings,
  get_return_valuations: executeGetReturnValuations,
};

const createToolRegistry = (): ToolRegistry => {
  const tools = new Map<string, ToolDefinition>();

  // Register all portfolio tools
  for (const schema of portfolioToolSchemas) {
    const executor = toolExecutors[schema.name];
    if (executor) {
      tools.set(schema.name, {
        ...schema,
        execute: executor,
      });
    }
  }

  const register = (tool: ToolDefinition): void => {
    tools.set(tool.name, tool);
  };

  const get = (name: string): ToolDefinition | undefined => {
    return tools.get(name);
  };

  const getSchemas = (): ToolSchema[] => {
    return Array.from(tools.values()).map(({ name, description, parameters }) => ({
      name,
      description,
      parameters,
    }));
  };

  const execute = async (name: string, args: Record<string, unknown>): Promise<ToolExecutionResult> => {
    const tool = tools.get(name);
    if (!tool) {
      return {
        success: false,
        error: `Unknown tool: ${name}`,
      };
    }

    try {
      return await tool.execute(args);
    } catch (error) {
      return {
        success: false,
        error: `Tool execution failed: ${(error as Error).message}`,
      };
    }
  };

  return {
    tools,
    register,
    get,
    getSchemas,
    execute,
  };
};

// Singleton instance
let registryInstance: ToolRegistry | null = null;

export const getToolRegistry = (): ToolRegistry => {
  if (!registryInstance) {
    registryInstance = createToolRegistry();
  }
  return registryInstance;
};
